/**
 * Storefront API Coupon Manager for the minicart(ec-coupon-cart.liquid in the cart-drawer.liquid)
 * Handles coupon validation and application using Shopify's Storefront API
 */

class StorefrontCouponManager {
  constructor(storefrontToken) {
    this.storefrontToken = storefrontToken;
    this.apiVersion = '2024-01';
    this.cartId = null;
    this.STORAGE_KEY = 'shopify_cart_id';
    this.COUPON_STORAGE_KEY = 'cart_coupon_code';
  }

  /**
   * Get Storefront API endpoint
   */
  getStorefrontApiUrl() {
    const shopDomain = window.Shopify?.shop || window.location.hostname;
    return `https://${shopDomain}/api/${this.apiVersion}/graphql.json`;
  }

  /**
   * Make GraphQL request to Storefront API
   */
  async graphqlRequest(query, variables = {}) {
    try {
      const response = await fetch(this.getStorefrontApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
        },
        body: JSON.stringify({ query, variables }),
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL request failed');
      }

      return result.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get or create cart ID from sessionStorage
   */
  getCartId() {
    if (!this.cartId) {
      this.cartId = sessionStorage.getItem(this.STORAGE_KEY);
    }
    return this.cartId;
  }

  /**
   * Save cart ID to sessionStorage
   */
  saveCartId(cartId) {
    this.cartId = cartId;
    sessionStorage.setItem(this.STORAGE_KEY, cartId);
  }

  /**
   * Fetch existing cart from Storefront API
   */
  async fetchCart(cartId) {
    const query = `
      query GetCart($cartId: ID!) {
        cart(id: $cartId) {
          id
          checkoutUrl
          discountCodes {
            code
            applicable
          }
          discountAllocations {
            discountedAmount {
              amount
              currencyCode
            }
            targetType
            ... on CartAutomaticDiscountAllocation {
              title
            }
            ... on CartCodeDiscountAllocation {
              code
            }
          }
          cost {
            subtotalAmount {
              amount
              currencyCode
            }
            totalAmount {
              amount
              currencyCode
            }
          }
          lines(first: 100) {
            edges {
              node {
                id
                quantity
                cost {
                  totalAmount {
                    amount
                    currencyCode
                  }
                }
                discountAllocations {
                  discountedAmount {
                    amount
                    currencyCode
                  }
                  ... on CartCodeDiscountAllocation {
                    code
                  }
                }
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    product {
                      title
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const data = await this.graphqlRequest(query, { cartId });
      return data.cart;
    } catch (error) {
      return null;
    }
  }

  /**
   * Create new cart via Storefront API
   */
  async createCart(ajaxCart = null) {
    // First, get current Ajax API cart to sync items
    ajaxCart = ajaxCart || await this.getAjaxCart();
    
    const lines = ajaxCart.items.map(item => ({
      merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
      quantity: item.quantity,
    }));

    const mutation = `
      mutation CreateCart($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            id
            checkoutUrl
            discountCodes {
              code
              applicable
            }
            cost {
              subtotalAmount {
                amount
                currencyCode
              }
              totalAmount {
                amount
                currencyCode
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    try {
      const data = await this.graphqlRequest(mutation, {
        input: { lines: lines.length > 0 ? lines : [] }
      });

      if (data.cartCreate.userErrors && data.cartCreate.userErrors.length > 0) {
        throw new Error(data.cartCreate.userErrors[0].message);
      }

      const cart = data.cartCreate.cart;
      this.saveCartId(cart.id);
      return cart;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get current cart via Ajax API (for syncing)
   */
  async getAjaxCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      return { items: [] };
    }
  }

  /**
   * Clear saved Storefront cart ID.
   */
  clearCartId() {
    this.cartId = null;
    sessionStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Normalize cart lines so Ajax cart and Storefront cart can be compared.
   */
  getAjaxLineSignature(ajaxCart) {
    const lines = new Map();

    (ajaxCart.items || []).forEach(item => {
      const variantId = String(item.variant_id);
      lines.set(variantId, (lines.get(variantId) || 0) + item.quantity);
    });

    return lines;
  }

  getStorefrontLineSignature(storefrontCart) {
    const lines = new Map();
    const edges = storefrontCart?.lines?.edges || [];

    edges.forEach(edge => {
      const node = edge.node;
      const merchandiseId = node?.merchandise?.id || '';
      const variantId = merchandiseId.split('/').pop();

      if (variantId) {
        lines.set(variantId, (lines.get(variantId) || 0) + node.quantity);
      }
    });

    return lines;
  }

  cartMatchesAjaxCart(storefrontCart, ajaxCart) {
    const ajaxLines = this.getAjaxLineSignature(ajaxCart);
    const storefrontLines = this.getStorefrontLineSignature(storefrontCart);

    if (ajaxLines.size !== storefrontLines.size) {
      return false;
    }

    for (const [variantId, quantity] of ajaxLines.entries()) {
      if (storefrontLines.get(variantId) !== quantity) {
        return false;
      }
    }

    return true;
  }

  async recreateCartFromAjax(ajaxCart = null) {
    this.clearCartId();
    return await this.createCart(ajaxCart);
  }

  /**
   * Get or create cart (main entry point)
   * Fetches existing cart if available, otherwise creates new one
   */
  async getOrCreateCart(options = {}) {
    const ajaxCart = await this.getAjaxCart();

    if (options.forceRecreate) {
      return await this.recreateCartFromAjax(ajaxCart);
    }

    const cartId = this.getCartId();
    
    if (cartId) {
      const cart = await this.fetchCart(cartId);
      if (cart && this.cartMatchesAjaxCart(cart, ajaxCart)) {
        // Cart exists and matches the visible Ajax cart.
        return cart;
      }
    }

    // If no cart exists, fetch failed, or cart lines are stale, create a fresh one.
    return await this.recreateCartFromAjax(ajaxCart);
  }

  /**
   * Apply discount code to cart
   */
  async applyDiscountCode(discountCode) {
    const code = discountCode.trim().toUpperCase();
    
    if (!code) {
      return {
        success: false,
        message: 'Por favor, digite um cupom',
        type: 'error'
      };
    }

    try {
      // Create a fresh Storefront cart from the visible Ajax cart before validating.
      const cart = await this.getOrCreateCart({ forceRecreate: true });
      
      if (!cart || !cart.id) {
        throw new Error('Não foi possível obter o carrinho');
      }

      // Apply discount code
      const mutation = `
        mutation ApplyDiscount($cartId: ID!, $discountCodes: [String!]!) {
          cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
            cart {
              id
              checkoutUrl
              discountCodes {
                code
                applicable
              }
              discountAllocations {
                discountedAmount {
                  amount
                  currencyCode
                }
                targetType
                ... on CartAutomaticDiscountAllocation {
                  title
                }
                ... on CartCodeDiscountAllocation {
                  code
                }
              }
              cost {
                subtotalAmount {
                  amount
                  currencyCode
                }
                totalAmount {
                  amount
                  currencyCode
                }
              }
              lines(first: 100) {
                edges {
                  node {
                    id
                    quantity
                    cost {
                      totalAmount {
                        amount
                        currencyCode
                      }
                    }
                    discountAllocations {
                      discountedAmount {
                        amount
                        currencyCode
                      }
                      ... on CartCodeDiscountAllocation {
                        code
                      }
                    }
                    merchandise {
                      ... on ProductVariant {
                        id
                      }
                    }
                  }
                }
              }
            }
            userErrors {
              field
              message
              code
            }
          }
        }
      `;

      const data = await this.graphqlRequest(mutation, {
        cartId: cart.id,
        discountCodes: [code]
      });

      const result = data.cartDiscountCodesUpdate;

      // Check for user errors
      if (result.userErrors && result.userErrors.length > 0) {
        const error = result.userErrors[0];
        return {
          success: false,
          message: this.getErrorMessage(error.code, error.message, code),
          type: 'error',
          cart: result.cart
        };
      }

      // Check if discount was applied
      const updatedCart = result.cart;
      const discountInfo = updatedCart.discountCodes.find(d => d.code === code);

      if (!discountInfo) {
        return {
          success: false,
          message: `Cupom "${code}" não pôde ser aplicado`,
          type: 'error',
          cart: updatedCart
        };
      }

      if (!discountInfo.applicable) {
        return {
          success: false,
          message: this.getNotApplicableMessage(code),
          type: 'error',
          cart: updatedCart
        };
      }

      // Success! Discount applied
      const discountAmount = this.calculateDiscountAmount(updatedCart);
      sessionStorage.setItem(this.COUPON_STORAGE_KEY, code);

      return {
        success: true,
        message: `Cupom <span class="coupon-code-bold">${code}</span> aplicado!`,
        type: 'success',
        code: code,
        cart: updatedCart
      };

    } catch (error) {
      return {
        success: false,
        message: 'Erro ao aplicar cupom. Tente novamente.',
        type: 'error'
      };
    }
  }

  /**
   * Remove discount code from cart
   */
  async removeDiscountCode() {
    try {
      const cart = await this.getOrCreateCart();
      
      if (!cart || !cart.id) {
        throw new Error('Não foi possível obter o carrinho');
      }

      const mutation = `
        mutation RemoveDiscount($cartId: ID!) {
          cartDiscountCodesUpdate(cartId: $cartId, discountCodes: []) {
            cart {
              id
              discountCodes {
                code
                applicable
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const data = await this.graphqlRequest(mutation, {
        cartId: cart.id
      });

      sessionStorage.removeItem(this.COUPON_STORAGE_KEY);

      return {
        success: true,
        message: 'Cupom removido',
        type: 'success'
      };

    } catch (error) {
      return {
        success: false,
        message: 'Erro ao remover cupom',
        type: 'error'
      };
    }
  }

  /**
   * Calculate discount amount from cart
   */
  calculateDiscountAmount(cart) {
    if (!cart.discountAllocations || cart.discountAllocations.length === 0) {
      return null;
    }

    const totalDiscount = cart.discountAllocations.reduce((sum, allocation) => {
      return sum + parseFloat(allocation.discountedAmount.amount);
    }, 0);

    if (totalDiscount === 0) return null;

    return totalDiscount.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  /**
   * Build a normalized coupon state from a Storefront cart, isolating the
   * coupon's real discount amount from automatic (product) promotions so the
   * minicart shows exactly what the checkout will charge.
   */
  getCouponState(cart, code) {
    const normalizedCode = (code || '').trim().toUpperCase();
    const allocations = cart?.discountAllocations || [];

    const couponAllocations = allocations.filter(
      (allocation) => allocation.code && allocation.code.toUpperCase() === normalizedCode
    );

    const couponAmount = couponAllocations.reduce(
      (sum, allocation) => sum + parseFloat(allocation.discountedAmount.amount || 0),
      0
    );

    const isFreeShipping = couponAllocations.some((allocation) => allocation.targetType === 'SHIPPING_LINE');

    const discountEntry = cart?.discountCodes?.find((d) => d.code?.toUpperCase() === normalizedCode);

    const lines = (cart?.lines?.edges || []).map((edge) => {
      const node = edge.node;
      const variantId = (node.merchandise?.id || '').split('/').pop();
      const lineCouponAmount = (node.discountAllocations || [])
        .filter((allocation) => allocation.code && allocation.code.toUpperCase() === normalizedCode)
        .reduce((sum, allocation) => sum + parseFloat(allocation.discountedAmount.amount || 0), 0);

      return {
        variantId,
        quantity: node.quantity,
        lineTotal: parseFloat(node.cost?.totalAmount?.amount || 0),
        couponAmountForLine: lineCouponAmount,
      };
    });

    return {
      code: normalizedCode,
      applicable: discountEntry?.applicable ?? false,
      couponAmount,
      isFreeShipping,
      totalAmount: parseFloat(cart?.cost?.totalAmount?.amount || 0),
      subtotalAmount: parseFloat(cart?.cost?.subtotalAmount?.amount || 0),
      currencyCode: cart?.cost?.totalAmount?.currencyCode || 'BRL',
      checkoutUrl: cart?.checkoutUrl,
      lines,
    };
  }

  /**
   * Get user-friendly error message
   */
  getErrorMessage(errorCode, originalMessage, code) {
    const errorMessages = {
      'DISCOUNT_NOT_FOUND': `Cupom "${code}" não é válido`,
      'DISCOUNT_EXPIRED': `Cupom "${code}" expirou`,
      'DISCOUNT_DISABLED': `Cupom "${code}" não está ativo`,
      'DISCOUNT_LIMIT_REACHED': `Cupom "${code}" atingiu o limite de uso`,
      'CUSTOMER_NOT_ELIGIBLE': 'Você não é elegível para este cupom',
      'CART_DOES_NOT_MEET_REQUIREMENTS': 'Carrinho não atende aos requisitos mínimos',
    };

    return errorMessages[errorCode] || `Cupom "${code}" não pôde ser aplicado: ${originalMessage}`;
  }

  /**
   * Get message for non-applicable discount
   */
  getNotApplicableMessage(code) {
    return `Cupom "${code}" não é aplicável ao seu carrinho atual. Verifique se o cupom expirou e os requisitos mínimos (valor mínimo, produtos específicos, etc.)`;
  }

  /**
   * Ensure Storefront cart has the same discount code as the Ajax cart (e.g. after checkout return).
   */
  async syncDiscountFromAjax(code) {
    const normalizedCode = code.trim().toUpperCase();
    if (!normalizedCode) {
      return { success: false };
    }

    try {
      const cart = await this.getOrCreateCart();
      const existing = cart?.discountCodes?.find(
        (d) => d.code?.toUpperCase() === normalizedCode && d.applicable
      );

      if (existing) {
        sessionStorage.setItem(this.COUPON_STORAGE_KEY, normalizedCode);
        return { success: true, cart };
      }

      return await this.applyDiscountCode(normalizedCode);
    } catch (error) {
      return { success: false };
    }
  }

  /**
   * Sync Ajax cart with Storefront cart
   * Call this when items are added/removed via Ajax API
   */
  async syncCart() {
    try {
      const cartId = this.getCartId();
      
      if (!cartId) {
        // Create new cart if none exists
        await this.createCart();
        return;
      }

      const ajaxCart = await this.getAjaxCart();
      const storefrontCart = await this.fetchCart(cartId);

      if (!storefrontCart || !this.cartMatchesAjaxCart(storefrontCart, ajaxCart)) {
        // Recreate cart if fetch failed or if Ajax cart items/quantities changed.
        await this.recreateCartFromAjax(ajaxCart);
        return;
      }

    } catch (error) {
      // Error syncing cart
    }
  }
}

// Initialize global instance
window.StorefrontCouponManager = StorefrontCouponManager;
